import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { getLocalizedText } from './MultiLanguageInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache for site settings to avoid multiple fetches
let cachedSiteSettings = null;

export const SEOHead = ({ page, pool, customTitle, customDescription }) => {
  const [siteSettings, setSiteSettings] = useState(cachedSiteSettings || {
    app_name: '',
    logo: '',
    favicon: ''
  });
  const [seoSettings, setSeoSettings] = useState({
    site_title: '',
    site_description: '',
    og_image_url: ''
  });
  const [isLoaded, setIsLoaded] = useState(!!cachedSiteSettings);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch site settings (name, logo, colors)
        if (!cachedSiteSettings) {
          const siteRes = await axios.get(`${API}/site-settings`);
          if (siteRes.data) {
            cachedSiteSettings = siteRes.data;
            setSiteSettings(siteRes.data);
          }
        }
        
        // Fetch SEO settings
        const seoRes = await axios.get(`${API}/seo`);
        if (seoRes.data) {
          setSeoSettings(seoRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch settings');
      } finally {
        setIsLoaded(true);
      }
    };
    fetchSettings();
  }, []);

  // Force update document title and meta description when pool changes
  useEffect(() => {
    if (!isLoaded || !siteSettings.app_name) return;
    
    if (pool && pool.name) {
      const appName = siteSettings.app_name;
      const poolName = getLocalizedText(pool.name, 'en');
      const poolDesc = getLocalizedText(pool.description, 'en') || '';
      const poolTitle = pool.meta_title || `${poolName} | ${appName}`;
      const poolDescription = pool.meta_description || poolDesc;
      document.title = poolTitle;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', poolDescription);
      }
      
      // Update OG tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', poolTitle);
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', poolDescription);
      
      let ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg && pool.image_url) ogImg.setAttribute('content', pool.image_url);
    }
  }, [pool, siteSettings.app_name, isLoaded]);

  // Compute SEO values
  const seoValues = useMemo(() => {
    if (!isLoaded || !siteSettings.app_name) {
      return null;
    }

    const appName = siteSettings.app_name;
    
    // Default values using dynamic app name
    const defaultTitle = seoSettings.site_title || `${appName} - Play Web3 Gacha and Get Rewards`;
    const defaultDescription = seoSettings.site_description || '';

    // Page-specific SEO with dynamic app name
    const getPageSEO = (pageKey) => {
      const pageSEO = {
        home: {
          title: seoSettings.site_title || `${appName} - Play Web3 Gacha and Get Rewards`,
          description: seoSettings.site_description || ''
        },
        pools: {
          title: `Gacha Pools | ${appName}`,
          description: seoSettings.site_description || ''
        },
        shop: {
          title: `Shop | ${appName}`,
          description: seoSettings.site_description || ''
        },
        inventory: {
          title: `My Collection | ${appName}`,
          description: seoSettings.site_description || ''
        },
        collection: {
          title: `My Collection | ${appName}`,
          description: seoSettings.site_description || ''
        },
        wallet: {
          title: `Wallet | ${appName}`,
          description: seoSettings.site_description || ''
        },
        staking: {
          title: `Staking | ${appName}`,
          description: seoSettings.site_description || ''
        },
        profile: {
          title: `Profile | ${appName}`,
          description: seoSettings.site_description || ''
        },
        login: {
          title: `Login | ${appName}`,
          description: seoSettings.site_description || ''
        },
        register: {
          title: `Sign Up | ${appName}`,
          description: seoSettings.site_description || ''
        }
      };
      return pageSEO[pageKey] || null;
    };

    // Determine title and description
    let title = defaultTitle;
    let description = defaultDescription;

    // Use custom values if provided
    if (customTitle) {
      title = customTitle;
    } else if (pool && pool.name) {
      // Pool page - use meta_title if set, otherwise use localized pool.name
      const poolName = getLocalizedText(pool.name, 'en');
      const poolDesc = getLocalizedText(pool.description, 'en') || '';
      title = pool.meta_title || `${poolName} | ${appName}`;
      // Use meta_description if set, otherwise use pool.description
      description = pool.meta_description || poolDesc;
    } else if (page) {
      const pageSEO = getPageSEO(page);
      if (pageSEO) {
        title = pageSEO.title;
        description = pageSEO.description;
      }
    }

    if (customDescription) {
      description = customDescription;
    }

    const ogImage = seoSettings.og_image_url || '';

    return { title, description, ogImage, favicon: siteSettings.favicon };
  }, [isLoaded, siteSettings, seoSettings, page, pool, customTitle, customDescription]);

  // Set document title directly when seoValues change
  useEffect(() => {
    if (seoValues?.title) {
      document.title = seoValues.title;
    }
  }, [seoValues]);

  // Don't render SEO tags until data is loaded
  if (!seoValues) {
    return null;
  }

  const { title, description, ogImage, favicon } = seoValues;

  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {(pool?.image_url || ogImage) && (
        <meta property="og:image" content={pool?.image_url || ogImage} />
      )}
      <meta property="og:type" content="website" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      {(pool?.image_url || ogImage) && (
        <meta name="twitter:image" content={pool?.image_url || ogImage} />
      )}
      
      {/* Favicon from site settings */}
      {favicon && (
        <link rel="icon" href={favicon} />
      )}
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
    </Helmet>
  );
};

export default SEOHead;
